/**
 * Shell copy for `src/lib` (keys: `shell.lib.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  apiAccountIntelligence: {
    linkedinManual: 'LinkedIn (manual)',
    reference: 'Reference',
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
    errorStatus: 'Error {{status}}',
    theAnalysisFailedTryAgain: 'The analysis failed. Try again.',
    phone: 'Phone',
    event: 'Event',
  },
  apiAddons: {
    amountCredit: '{{amount}} / credit',
  },
  apiAds: {
    draft: 'Draft',
    paused: 'Paused',
    active: 'Active',
    archived: 'Archived',
    enable: 'Enable',
    pause: 'Pause',
    archive: 'Archive',
    awareness: 'Awareness',
    scope: 'Scope',
    traffic: 'Traffic',
    engagement: 'Engagement',
    leads: 'Leads',
    appInstalls: 'App installs',
    sales: 'Sales',
    pageLikesFollowers: 'Page likes (followers)',
    daily: 'Daily',
    lifetimeTotal: 'Lifetime total',
    image: 'Image',
    video: 'Video',
    carousel: 'Carousel',
    textOnly: 'Text only',
    feed: 'Feed',
    stories: 'Stories',
    reels: 'Reels',
    lowestCost: 'Lowest cost',
    costCap: 'Cost cap',
    bidCap: 'Bid cap',
    vConnection: '{{v}} connection',
    errorStatus: 'Error {{status}}',
    theActionFailed: 'The action failed.',
    thisFeatureIsNotIncludedIn: 'This feature is not included in your plan — upgrade to a higher plan to access it.',
    adSets: 'Ad sets',
    ads: 'Ads',
    creatives: 'Creatives',
    age: 'Age',
    gender: 'Gender',
    spend: 'Spend',
    impressions: 'Impressions',
    clicks: 'Clicks',
    conversions: 'Conversions',
    inReview: 'In review',
    thePlatformIsReviewingThisAd: 'The platform is reviewing this ad.',
    approved: 'Approved',
    adApprovedByThePlatform: 'Ad approved by the platform.',
    delivered: 'Delivered',
    adApprovedAndRunning: 'Ad approved and running.',
    rejectedWhy: 'Rejected: {{why}}',
    rejected: 'Rejected',
    adRejectedByThePlatform: 'Ad rejected by the platform.',
    issuesWhy: 'Issues: {{why}}',
    withIssues: 'With issues',
    thePlatformReportsIssuesWithThis: 'The platform reports issues with this ad.',
    reviewPaused: 'Review: paused',
    adPausedOnThePlatformSide: 'Ad paused on the platform side.',
    pauseTheCampaign: 'Pause the campaign',
    notifyOnly: 'Notify only',
    rule: 'Rule',
    search: 'Search',
    country: 'Country',
    region: 'Region',
  },
  apiAdsCommand: {
    onPlatform: ' on {{on}}',
    notSupported: 'not supported',
    theApiDoesNotReturnLikes: 'the API does not return Likes',
    notOfferedByThePlatform: 'not offered by the platform',
    limitedToYourOwnMedia: 'limited to your own media',
    awaitingAppReview: 'awaiting app review',
    requiresThePartnerProgram: 'requires the partner program',
    requiresElevatedAccess: 'requires elevated access',
    permissionDeniedByThePlatform: 'permission denied by the platform',
    notYetAvailableOnThisPlatform: 'not yet available on this platform',
    noDataMeasuredYet: 'no data measured yet',
    available: 'Available',
    partnerAccessRequired: 'Partner access required',
    notAvailable: 'Not available',
    neverRefreshed: 'Never refreshed',
    unknownDate: 'Unknown date',
    justNow: 'just now',
    minsMinAgo: '{{mins}} min ago',
    vHAgo: '{{v}} h ago',
    vDAgo: '{{v}} d ago',
    refreshedText: 'Refreshed {{text}}',
    likes: 'Likes',
    love: 'Love',
    care: 'Care',
    wow: 'Wow',
    haha: 'Haha',
    sad: 'Sad',
    angry: 'Angry',
    thankful: 'Thankful',
    pride: 'Pride',
    aboveAverage: 'Above average',
    average: 'Average',
    belowAverageBottom35: 'Below average (bottom 35%)',
    belowAverageBottom20: 'Below average (bottom 20%)',
    belowAverageBottom10: 'Below average (bottom 10%)',
    noDataYet: 'No data yet',
  },
  apiAdvisor: {
    contentPublishing: 'Content & publishing',
    ads: 'Ads',
    prospecting: 'Prospecting',
    nurturing: 'Nurturing',
    campaigns: 'Campaigns',
  },
  apiAdvocacy: {
    xTwitter: 'X / Twitter',
    copyLink: 'Copy link',
    youMustFirstAgreeToTake: 'You must first agree to take part (consent).',
    youHaveLeftTheProgram: 'You have left the program.',
    thisCampaignIsNotActive: 'This campaign is not active.',
    thisCampaignIsArchived: 'This campaign is archived.',
    email: 'Email',
    toDiscover: 'To discover',
  },
  apiAiContent: {
    couldNotReadTheFile: 'Could not read the file.',
  },
  apiAiSuite: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    thisAiCapabilityIsNotYet: 'This AI capability is not yet available on this backend.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance in Billing.',
  },
  apiAssistant: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to generate a draft.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again — nothing was made up.',
    thisCapabilityIsNotYetAvailable: 'This capability is not yet available on this backend.',
  },
  apiAssistantAct: {
    actionV: 'Action {{v}}',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    personalAiCapReachedAskAn: 'Personal AI cap reached — ask an administrator to increase your budget.',
    aiCreditsTemporarilyUnavailableTryAgain: 'AI credits temporarily unavailable — try again in a moment.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to propose a plan.',
    theAiTeamIsTemporarilyUnavailable: 'The AI team is temporarily unavailable. Try again — nothing was made up.',
    thisCapabilityIsNotYetAvailable: 'This capability is not yet available on this backend.',
    generate: 'Generate',
    contentCreatives: 'Content & creatives',
    postsCaptionsCarouselsVisualsMultiPlatfo: 'Posts, captions, carousels, visuals, multi-platform variants.',
    create: 'Create',
    campaignsSequences: 'Campaigns & sequences',
    adsNurturingProspectingSequencesForms: 'Ads, nurturing, prospecting sequences, forms.',
    schedule: 'Schedule',
    strategyCalendar: 'Strategy & calendar',
    marketingStrategyGtmPlanEditorialAnd: 'Marketing strategy, GTM plan, editorial and prospecting calendar.',
    organize: 'Organize',
    crmCommunity: 'CRM & community',
    contactsSegmentsTaskQueueInboxModeration: 'Contacts, segments, task queue, inbox, moderation.',
    optimize: 'Optimize',
    analysisRecommendations: 'Analysis & recommendations',
    resultsMeasurementABBestTimes: 'Results measurement, A/B, best times, improvement recommendations.',
  },
  apiAutomations: {
    engagementThresholdAbsolute: 'Engagement threshold (absolute)',
    relativeToTheAccountAverageRecommended: 'Relative to the account average (recommended)',
    addACommentWithACall: 'Add a comment with a call to action',
    boostThePostMeta: 'Boost the post (Meta)',
    recycleThePost: 'Recycle the post',
    suggestDoesNothingWithoutYou: 'Suggest (does nothing without you)',
    prepareADraft: 'Prepare a draft',
    automaticAfterSimulationComplianceFilter: 'Automatic (after simulation + compliance filter)',
  },
  apiB2b: {
    firstName: 'First name',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    jobTitle: 'Job title',
    company: 'Company',
    companyDomain: 'Company domain',
    seniority: 'Seniority',
    department: 'Department',
    city: 'City',
    region: 'Region',
    country: 'Country',
    postalCode: 'Postal code',
    address: 'Address',
    domain: 'Domain',
    website: 'Website',
    industry: 'Industry',
    headcount: 'Headcount',
    employees: 'Employees',
    annualRevenue: 'Annual revenue',
    yearFounded: 'Year founded',
    headquarters: 'Headquarters',
    description: 'Description',
    cLevel: 'C-level',
    vp: 'VP',
    director: 'Director',
    manager: 'Manager',
    senior: 'Senior',
    junior: 'Junior',
    founder: 'Founder',
    partner: 'Partner',
    other: 'Other',
  },
  apiB2bInfluencers: {
    identified: 'Identified',
    contacted: 'Contacted',
    negotiation: 'Negotiation',
    online: 'Online',
    delivered: 'Delivered',
    measured: 'Measured',
    clicks: 'Clicks',
    leads: 'Leads',
    conversions: 'Conversions',
    revenue: 'Revenue',
    scope: 'Scope',
    noTrackedLinkOrPromoCode: 'No tracked link or promo code configured — results cannot be attributed.',
    unattributedUnattributedEventS: '{{unattributed}} unattributed event(s).',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsReadOnly: 'Access denied — insufficient rights (read-only role).',
    membersPerStage: 'members per stage',
  },
  apiBioBlocks: {
    heroTitle: 'Hero (title)',
    text: 'Text',
    video: 'Video',
    form: 'Form',
    quiz: 'Quiz',
    product: 'Product',
    testimonial: 'Testimonial',
    countdown: 'Countdown',
    actionButtonCta: 'Action button (CTA)',
    consent: 'Consent',
    productGrid: 'Product grid',
    exitIntentTheMouseLeavesThe: 'Exit intent (the mouse leaves the page)',
    scrollOfThePage: 'Scroll (% of the page)',
    delaySeconds: 'Delay (seconds)',
    yourTitle: 'Your title',
    yourTextHere: 'Your text here.',
    iAgreeToReceiveEmailsI: 'I agree to receive emails. I can unsubscribe at any time.',
    theOfferEndsIn: 'The offer ends in',
    takeAction: 'Take action',
    weOnlyUseYourEmailFor: 'We only use your email for this offer and its follow-ups.',
    buy: 'Buy',
    send: 'Send',
    waitlist: 'Waitlist',
  },
  apiByok: {
    ollamaSelfHosted: 'Ollama (self-hosted)',
  },
  apiCanvas: {
    expandAndEnrichTheTargetedPassage: 'Expand and enrich the targeted passage below with more detail, examples and precision, without changing its meaning or tone. Reply ONLY with the expanded passage, in markdown, with no comment or added heading.',
    rewriteTheTargetedPassageBelowTo: 'Rewrite the targeted passage below to make it shorter and denser, keeping the essential meaning and the tone. Reply ONLY with the shortened passage, in markdown, with no comment.',
    rewriteTheTargetedPassageBelowExpanding: 'Rewrite the targeted passage below expanding it significantly (longer), keeping the same meaning and the same tone. Reply ONLY with the rewritten passage, in markdown, with no comment.',
    improveTheStyleClarityAndFlow: 'Improve the style, clarity and flow of the targeted passage below, without changing its meaning. Reply ONLY with the improved passage, in markdown, with no comment.',
    naturallyContinueWritingTheDocumentRight: 'Naturally continue writing the document right after the targeted passage below, in the same tone and style, for one or two paragraphs. Reply ONLY with the continuation of the text, in markdown, without repeating what precedes and with no comment.',
    noSelectionContinueAtTheEnd: '(no selection — continue at the end of the document)',
    documentContextMayBeTruncated: '--- Document context (may be truncated) ---',
    emptyDocument: '(empty document)',
    targetedPassage: '--- Targeted passage ---',
  },
  apiClient: {
    apiErrorStatus: 'API error {{status}}',
    somethingWentWrongPleaseTryAgain: 'Something went wrong. Please try again.',
    sessionExpiredSignInAgainThen: 'Session expired. Sign in again, then try again.',
    actionReservedForTheAccountOwners: 'Action reserved for the account owners and administrators.',
    itemNotFoundItMayHave: 'Item not found — it may have been deleted. Refresh the page.',
    conflictThisItemAlreadyExistsOr: 'Conflict: this item already exists or a limit has been reached.',
    invalidDataCheckTheFieldsEntered: 'Invalid data — check the fields entered.',
    theServiceRanIntoAProblem: 'The service ran into a problem. Try again in a moment.',
    serviceTemporarilyUnavailableCheckYourCo: 'Service temporarily unavailable. Check your connection and try again.',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
    tryAgainInRetryafterS: ' (try again in {{retryAfter}}s)',
  },
  apiCommerce: {
    metaCatalog: 'Meta catalog',
  },
  apiCommunities: {
    internalAmbassador: 'Internal ambassador',
    externalUser: 'External user',
    guest: 'Guest',
    active: 'Active',
    withdrawn: 'Withdrawn',
    youMustFirstAgreeToTake: 'You must first agree to take part (consent).',
    youHaveLeftTheGroup: 'You have left the group.',
    yourMembershipIsNotActive: 'Your membership is not active.',
    youAreNotAnAmplifierIn: 'You are not an amplifier in this group.',
    theGroupHasTriggeredAnEmergency: 'The group has triggered an emergency stop — no action for now.',
    automaticEngagementIsDisabledForThis: 'Automatic engagement is disabled for this group.',
    textBlockedByTheComplianceFilter: 'Text blocked by the compliance filter.',
    actionPostponedQuietHoursInEffect: 'Action postponed: quiet hours in effect.',
    dailyCapReachedForThisMember: 'Daily cap reached for this member.',
    consentSourceInviteAccept: 'on acceptance',
    consentSourceMemberOptIn: 'by the member',
    consentSourceMemberWithdraw: 'withdrawn by the member',
    consentSourceAdminRemove: 'withdrawn (member removed)',
  },
  apiCommunity: {
    tag: 'Tag',
    addToGroup: 'Add to group',
    changeTheLifecycle: 'Change the lifecycle',
    trackASignal: 'Track a signal',
    enrichTheContact: 'Enrich the contact',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    highIntent: 'high intent',
    anyInteraction: 'Any interaction',
    allKeywords: 'all keywords',
    adRepliesOnly: 'ad replies only',
  },
  apiCommunityCoverage: {
    publish: 'Publish',
    comment: 'Comment',
    react: 'React',
    hide: 'Hide',
    delete: 'Delete',
    privateMessage: 'Private message',
    share: 'Share',
    collect: 'Collect',
    history: 'History',
    autoReaction: 'Auto reaction',
    available: 'Available',
    partnerAccessRequired: 'Partner access required',
    notAvailable: 'Not available',
    metaGroupsPublishingUnavailableDeprecate: 'Meta groups: publishing unavailable (deprecated API)',
    ownedMediaOnly: 'Owned media only',
    theGraphApiDoesNotAllow: 'The Graph API does not allow reactions',
    theDataApiDoesNotAllow: 'The Data API does not allow reactions',
    notOfferedByThePlatform: 'Not offered by the platform',
    partnerProgramRequired: 'Partner program required',
    awaitingAppReview: 'Awaiting app review',
    administratorRightsRequired: 'Administrator rights required',
    elevatedAccessRequired: 'Elevated access required',
    n24HSessionWindow: '24 h session window',
    unknownPlatformOrAction: 'Unknown platform or action',
    like: 'Like',
    repost: 'Repost',
    videoOnly: 'Video only',
    replyOnly: 'Reply only',
    ownedPageROrganizationAdmin: 'Owned page (r_organization_admin)',
  },
  apiCommunityTargeting: {
    pasteTheUrlOfThePost: 'Paste the URL of the post to comment on / react to / share.',
    noAiWrittenContentWriteIt: 'No AI-written content — write it yourself.',
    promotionalContentBlockedNoPitchOn: 'Promotional content blocked (no pitch on a third-party page).',
    linkedinAutomationDisabledTosManualActio: 'LinkedIn: automation disabled (ToS) — manual action only.',
    tiktokNotSupported: 'TikTok: not supported.',
    metaDoesNotShareSilentlyOpen: 'Meta does not share silently — open the prepared share link.',
    thisTargetDoesNotAcceptPublishing: 'This target does not accept publishing.',
    publishingToMetaGroupsIsDeprecated: 'Publishing to Meta groups is deprecated.',
    targetNotFound: 'Target not found.',
    suggestionNotFound: 'Suggestion not found.',
    campaignSuspendedAfterConsecutiveErrors: 'Campaign suspended after consecutive errors.',
    autonomousModeForbiddenOnThisPlatform: 'Autonomous mode is forbidden on this platform (its terms of service) — manual action only.',
    sessionExpiredSignInToPublish: 'Session expired — sign in again to publish.',
    platformNotSupportedByPublishing: 'This platform is not supported by publishing.',
    publishingServiceUnavailableRetry: 'Publishing service unavailable — try again.',
  },
  apiCompliance: {
    serviceTemporarilyUnavailableTryAgain: 'Service temporarily unavailable. Try again.',
  },
  apiComposerPanels: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    theConnectedAccountDoesNotAllow: 'The connected account does not allow access to this data — reconnect it with the required permissions.',
    tooManyRequestsWaitAMoment: 'Too many requests — wait a moment, then try again.',
    thisFeatureIsNotYetAvailable: 'This feature is not yet available on this backend.',
    errorStatus: 'Error {{status}}',
    public: 'Public',
    friendsMutualFollows: 'Friends (mutual follows)',
    followers: 'Followers',
    privateOnlyMe: 'Private (only me)',
  },
  apiContentApprovals: {
    allUnanimous: 'All (unanimous)',
    anyOne: 'Any one',
    stepV: 'Step {{v}}',
    haveNeedApprovals: '{{have}} / {{need}} approvals',
    n1ApprovalReceived: '1 approval received',
    n1ApprovalRequired: '1 approval required',
    inProgress: 'In progress',
    approved: 'Approved',
    rejected: 'Rejected',
    skipped: 'Skipped',
    pending: 'Pending',
    approved2: 'Approved',
    dismissed: 'Dismissed',
    inReview: 'In review',
    notStarted: 'Not started',
    used: 'Used',
    revoked: 'Revoked',
    expired: 'Expired',
    active: 'Active',
    theApprovalChainIsNotYet: 'The approval chain is not yet deployed on this environment.',
    youAreNotAllowedToPerform: 'You are not allowed to perform this action.',
    thisStepHasAlreadyBeenHandled: 'This step has already been handled. Refresh the page.',
    eachStepMustDefineAtLeast: 'Each step must define at least one allowed role or a named approver.',
  },
  apiContentLocalize: {
    oneClickLocalizationIsNotYet: 'One-click localization is not yet deployed on this environment — coming soon.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to localize.',
    localizationRequiresAnAiModelKey: 'Localization requires an AI model key, and none is configured.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again — nothing was made up.',
  },
  apiContentPipeline: {
    stepByStepAiGenerationUnavailable: 'Step-by-step AI generation unavailable on this environment for now — enter your text manually below.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRights: 'Access denied — insufficient rights.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to generate proposals.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again in a moment.',
    engagingProfessionalContentIdeas: 'Engaging professional content ideas',
  },
  apiContentQueue: {
    xTwitter: 'X / Twitter',
    errorStatus: 'Error {{status}}',
    unknownError: 'Unknown error',
    sessionWithoutATenantSignIn: 'Session without a tenant — sign in again.',
    incompleteSessionTenantUserMissingSign: 'Incomplete session (tenant/user missing) — sign in again.',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  },
  apiConversation: {
    aiUnavailableReason: 'AI unavailable: {{reason}}',
    aiConversationAnalysisIsTemporarilyUnava: 'AI conversation analysis is temporarily unavailable. Try again later.',
  },
  apiCopilotSkills: {
    threadBuilderThreadBuilder: 'Thread builder (thread_builder)',
    bestTimeToPost: 'Best time to post',
    contentGapAnalysis: 'Content gap analysis',
    leadScoring: 'Lead scoring',
    audienceSegmentation: 'Audience segmentation',
  },
  apiCreativeAd: {
    product: 'Product',
    text: 'Text',
    video: 'Video',
    assembly: 'Assembly',
    draft: 'Draft',
  },
  apiCrm: {
    text: 'Text',
    count: 'Count',
    listSingleChoice: 'List (single choice)',
    listMultipleChoice: 'List (multiple choice)',
    checkbox: 'Checkbox',
    address: 'Address',
    amountCurrency: 'Amount (currency)',
    rating05: 'Rating (0-5)',
    relationContactCompany: 'Relation (contact/company)',
    aiMagicComputed: 'AI (Magic) — computed',
    wonDealValue: 'Won deal value',
    dealsWon: 'Deals won',
    contactsAdded: 'Contacts added',
    activities: 'Activities',
    openPipelineValue: 'Open pipeline value',
    week: 'Week',
    quarter: 'Quarter',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    roundRobin: 'Round-robin',
    leastBusy: 'Least busy',
    setupRequiredCalendlyOauthCredentialsMis: 'Setup required: Calendly OAuth credentials missing on the server.',
    calendlyIsNotConnected: 'Calendly is not connected.',
    calendlyTokenUnavailableReconnectYourAcc: 'Calendly token unavailable — reconnect your account.',
    couldNotReadTheCalendlyProfile: 'Could not read the Calendly profile — reconnect your account.',
    calendlyRefusedToCreateTheWebhook: 'Calendly refused to create the webhook.',
    calendlyRefusedToDeleteTheWebhook: 'Calendly refused to delete the webhook.',
    noActiveCalendlyWebhook: 'No active Calendly webhook.',
    couldNotUpdateTheCalendlyWebhook: 'Could not update the Calendly webhook.',
    statusUnavailable: 'Status unavailable',
    connectedWho: 'Connected — {{who}}',
    connected: 'Connected',
    notConfiguredCalendlyOauthCredentialsReq: 'Not configured (Calendly OAuth credentials required)',
    notConnected: 'Not connected',
    longText: 'Long text',
    dropdownList: 'Dropdown list',
    firstName: 'First name',
    name: 'Name',
    jobTitle: 'Job title',
    customField: 'Custom field',
    quote: 'Quote',
    draft: 'Draft',
    sent: 'Sent',
    accepted: 'Accepted',
    declined: 'Declined',
    serviceTemporarilyUnavailable: 'Service temporarily unavailable.',
    read: 'Read',
    readWrite: 'Read/write',
    savedSearch: 'Saved search',
    prospectList: 'Prospect list',
    accountDossierAi: 'Account dossier (AI)',
    proposal: 'Proposal',
    linkUrl: 'Link (URL)',
    phone: 'Phone',
    labels: 'Labels',
    month: 'Month',
    year: 'Year',
  },
  apiCrmAutomations: {
    contactCreated: 'Contact created',
    fieldChanged: 'Field changed',
    dealStageChanged: 'Deal stage changed',
    unanswered: 'Unanswered',
    enrichAi: 'Enrich (AI)',
    assignAnOwner: 'Assign an owner',
    addToAGroup: 'Add to a group',
    notify: 'Notify',
    sendAnEmail: 'Send an email',
    aiDraft: 'AI draft',
    firesWhenAContactIsCreated: 'Fires when a contact is created.',
    firesWhenAFieldChangesValue: 'Fires when a field changes value.',
    firesWhenADealChangesStage: 'Fires when a deal changes stage.',
    firesAfterNDaysWithoutA: 'Fires after N days without a reply from a contact.',
    aiEnrichmentOfTheContactResearch: 'AI enrichment of the contact (research + note).',
    assignsAnOwnerToTheContact: 'Assigns an owner to the contact.',
    addsTheContactToACrm: 'Adds the contact to a CRM group.',
    sendsAnInternalNotification: 'Sends an internal notification.',
    sendsAnEmailToTheContact: 'Sends an email to the contact.',
    draftsAMessageWithAiHuman: 'Drafts a message with AI (human review).',
    draftsAMessageForThisContact: 'Drafts a message for this contact.',
    emailToSendSubjectVAutomatic: 'Email to send (subject: {{v}}) — automatic sending awaiting the email tool.',
    typeAction: '“{{type}}” action',
    addATriggerBeforeEnablingThe: 'Add a trigger before enabling the automation.',
    noReplySweepName: 'No reply — sweep ({{name}})',
    labelSpecifyTheWatchedField: '“{{label}}”: specify the watched field.',
    labelSpecifyTheDelayDays: '“{{label}}”: specify the delay (days).',
    labelChooseATargetGroup: '“{{label}}”: choose a target group.',
    labelSpecifyAnOwnerOrA: '“{{label}}”: specify an owner or a strategy.',
    labelTheMessageIsEmpty: '“{{label}}”: the message is empty.',
    labelTheEmailSubjectIsEmpty: '“{{label}}”: the email subject is empty.',
    theAutomationContainsNoNode: 'The automation contains no node.',
    duplicateNodeIdentifierId: 'Duplicate node identifier: “{{id}}”.',
    nodeIdUnknownTypeType: 'Node “{{id}}”: unknown type “{{type}}”.',
    edgeVUnknownSourceSource: 'Edge #{{v}}: unknown source “{{source}}”.',
    edgeVUnknownTargetTarget: 'Edge #{{v}}: unknown target “{{target}}”.',
    addAtLeastOneTrigger: 'Add at least one trigger.',
    anAutomationCanOnlyHaveOne: 'An automation can only have one trigger ({{length}} found).',
    addAtLeastOneAction: 'Add at least one action.',
    triggerIdMustNotHaveAn: 'Trigger “{{id}}” must not have an incoming edge.',
    actionIdIsNotConnectedTo: 'Action “{{id}}” is not connected to any trigger.',
    cycleDetectedV: 'Cycle detected: {{v}}',
  },
  apiCrmIo: {
    excelXlsx: 'Excel (.xlsx)',
    textTsv: 'Text (TSV)',
  },
  apiDelegations: {
    publish: 'Publish',
    ads: 'Ads',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    boost: 'Boost',
    delegatedAccountV: 'Delegated account · {{v}}',
    allPlatforms: ' · all platforms',
    denied: ' (denied)',
  },
  apiDesign: {
    flyer45: 'Flyer (4:5)',
    a4FlyerPortrait: 'A4 flyer (portrait)',
    instagramPost11: 'Instagram post (1:1)',
    story916: 'Story (9:16)',
    facebookPost1911: 'Facebook post (1.91:1)',
    youtubeThumbnail169: 'YouTube thumbnail (16:9)',
    custom: 'Custom',
    quote: 'Quote',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to use this function.',
    aiGenerationIsNotConfiguredOn: 'AI generation is not configured on this server.',
    yourRoleDoesNotAllowUsing: 'Your role does not allow using AI.',
    thisTemplateHasNoTextSlot: 'This template has no text slot to fill — add template text zones (slots) before generating.',
    theAiActionFailedTryAgain: 'The AI action failed. Try again.',
    flyer: 'Flyer',
    infographic: 'Infographic',
    carousel: 'Carousel',
    story: 'Story',
    announcement: 'Announcement',
    thumbnail: 'Thumbnail',
  },
  apiDesignStudio: {
    stockMediaSearchIsNotConfigured: 'Stock media search is not configured (no provider key on the server).',
  },
  apiDialer: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsToCall: 'Access denied — insufficient rights to call.',
    errorStatus: 'Error {{status}}',
    connected: 'Connected',
    voicemail: 'Voicemail',
    unanswered: 'Unanswered',
    busy: 'Busy',
    wrongNumber: 'Wrong number',
    notInterested: 'Not interested',
    callbackRequested: 'Callback requested',
    meetingBooked: 'Meeting booked',
    doNotCall: 'Do not call',
    failed: 'Failed',
    noOutcome: 'No outcome',
    queued: 'Queued',
    initiated: 'Initiated',
    ringing: 'Ringing',
    inCall: 'In call',
    done: 'Done',
    cancelled: 'Cancelled',
  },
  apiDiscovery: {
    person: 'Person',
    group: 'Group',
    media: 'Media',
    organization: 'Organization',
    company: 'Company',
    agency: 'Agency',
    government: 'Government',
    program: 'Program',
    other: 'Other',
    africa: 'Africa',
    antarctica: 'Antarctica',
    asia: 'Asia',
    europe: 'Europe',
    northAmerica: 'North America',
    oceania: 'Oceania',
    southAmerica: 'South America',
    aSearchRadiusRequiresAPoint: 'A search radius requires a point (latitude/longitude) or a postal code — enter one, or remove the radius.',
    openstreetmapNominatim: 'OpenStreetMap (Nominatim)',
    freeNoKeyPlacesShopsAnd: 'Free, no key — places, shops and public buildings (OSM)',
    googlePlaces: 'Google Places',
    addressesAndGeolocationTheCoreOf: 'Addresses and geolocation (the core of geo prospecting)',
    googleWebSearch: 'Google (web search)',
    officialCustomSearchApiIndexedCompany: 'Official Custom Search API — indexed company websites',
    officialRegistriesCompaniesGovernment: 'Official registries — companies, government',
    crunchbase: 'Crunchbase',
    organizationsFunding: 'Organizations, funding',
    apolloCompanies: 'Apollo — companies',
    companySearchFirmographics: 'Company search (firmographics)',
    corporationsCanadaFederal: 'Corporations Canada (federal)',
    officialFederalRegistryOpenDataSetup: 'Official federal registry (open data) — setup required',
    registraireDesEntreprisesQuebec: 'Registraire des entreprises (Quebec)',
    reqOpenDataSetupRequired: 'REQ open data — setup required',
    ontarioBusinessRegistry: 'Ontario Business Registry',
    ontarioOpenDataSetupRequired: 'Ontario open data — setup required',
    secEdgarUnitedStates: 'SEC EDGAR (United States)',
    freeOfficialRegistryOfUsListed: 'Free — official registry of US listed companies (United States only)',
    apolloPeopleSearch: 'Apollo — people search',
    peopleSearchTitleSenioritySchoolVia: 'People search (title, seniority, school…) via the official Apollo API',
    owner: 'Owner',
    founder: 'Founder',
    executiveCLevel: 'Executive (C-level)',
    partner: 'Partner',
    head: 'Head',
    director: 'Director',
    manager: 'Manager',
    senior: 'Senior',
    junior: 'Junior',
    intern: 'Intern',
  },
  apiDocumentVault: {
    invoiceReceived: 'Invoice received',
    invoiceIssued: 'Invoice issued',
    serviceRequest: 'Service request',
    offer: 'Offer',
    quote: 'Quote',
    contract: 'Contract',
    other: 'Other',
    uploaded: 'Uploaded',
    new: 'New',
    toHandle: 'To handle',
    pending: 'Pending',
    approved: 'Approved',
    paid: 'Paid',
    behind: 'Behind',
    accepted: 'Accepted',
    declined: 'Declined',
    signed: 'Signed',
    archived: 'Archived',
    serviceTemporarilyUnavailableTryAgain: 'Service temporarily unavailable. Try again.',
    invalidServerResponse: 'Invalid server response.',
    uploadFailedStatus: 'Upload failed ({{status}}).',
    networkErrorDuringUpload: 'Network error during upload.',
    email: 'Email',
  },
  apiEditorialProfiles: {
    casual: 'casual',
    educational: 'educational',
  },
  apiEmail: {
    replaceIncludeWithYourProviderS: 'Replace include: with your provider\'s SPF host (SES, SendGrid, SMTP…).',
    yourProviderGeneratesTheDkimKey: 'Your provider generates the DKIM key pair; paste its CNAME/TXT record.',
    startWithPNoneMonitoringThen: 'Start with p=none (monitoring), then move to quarantine/reject.',
  },
  apiGhostwriter: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to generate with the model.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again in a moment — nothing was made up.',
    thisCapabilityIsNotYetAvailable: 'This capability is not yet available on this backend.',
  },
  apiGrants: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    actionReservedForTheOwnerOr: 'Action reserved for the owner or an administrator — your role is read-only.',
    notFoundMaybeAlreadyHandled: 'Not found (maybe already handled).',
    thisRequestHasAlreadyBeenHandled: 'This request has already been handled (approved or rejected) — the list will be refreshed.',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
    tool: 'Tool',
    skill: 'Skill',
    pending: 'Pending',
    approved: 'Approved',
    dismissed: 'Dismissed',
    allStatuses: 'All statuses',
    sessionDurationNoTimeLimit: 'Session duration (no time limit)',
    n30Minutes: '30 minutes',
    n1Hour: '1 hour',
    n4Hours: '4 hours',
    n8Hours: '8 hours',
    n24Hours: '24 hours',
    n7Days: '7 days',
  },
  apiGrid: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to start the generation.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again in a moment — nothing was made up.',
    thisFeatureIsNotYetAvailable: 'This feature is not yet available / configured on this backend.',
    socialMediaPost: 'Social media post',
    onePostPerRowTopicPlatform: 'One post per row (topic, platform, call to action).',
    topic: 'Topic',
    launchOfTheNewOffer: 'Launch of the new offer',
    platform: 'Platform',
    callToAction: 'Call to action',
    bookADemo: 'Book a demo',
    productSheet: 'Product sheet',
    oneProductDescriptionPerRow: 'One product description per row.',
    product: 'Product',
    ariaErgonomicChair: 'Aria ergonomic chair',
    keyFeatures: 'Key features',
    adjustableBreathableFabric5YearWarranty: 'Adjustable, breathable fabric, 5-year warranty',
    audience: 'Audience',
    remoteWorkers: 'Remote workers',
    advertisingAd: 'Advertising (ad)',
    oneAdHookPerRow: 'One ad hook per row.',
    productOffer: 'Product / offer',
    onlineCrmTraining: 'Online CRM training',
    mainBenefit: 'Main benefit',
    save5HWeek: 'Save 5 h/week',
    freeTrial: 'Free trial',
    emailSubject: 'Email subject',
    oneSubjectLinePerRow: 'One subject line per row.',
    campaignTopic: 'Campaign topic',
    backToSchoolPromotion: 'Back-to-school promotion',
    existingCustomers: 'Existing customers',
    blogIntro: 'Blog intro',
    oneArticleIntroductionPerRow: 'One article introduction per row.',
    articleTitle: 'Article title',
    theCompleteGuideToMarketingAutomation: 'The complete guide to marketing automation',
    angle: 'Angle',
    smesWithoutATechnicalTeam: 'SMEs without a technical team',
  },
  apiImageEdit: {
    provideASourceImageMediaId: 'Provide a source image (media ID or URL).',
    aMaskIsRequiredDrawThe: 'A mask is required — draw the area to edit or import a mask.',
    describeWhatShouldFillTheMasked: 'Describe what should fill the masked area (prompt required in “fill” mode).',
    aiImageEditingIsNotYet: 'AI image editing is not yet configured (no provider key on the server).',
    aiInpaintingIsNotYetDeployed: 'AI inpainting is not yet deployed on this environment — coming soon.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to edit the image.',
    mediaNotFoundOrNotAllowed: 'Media not found (or not allowed for this account).',
    theReferencedMediaIsNotA: 'The referenced media is not a valid image.',
    theImageEditingProviderFailedTry: 'The image editing provider failed. Try again — nothing was made up.',
  },
  apiInboundRouters: {
    unreadableConditionClauseFormatsFieldVal: 'Unreadable condition: “{{clause}}”. Formats: field=value, field!=value, field~text, field in a|b, exists:field.',
    allLeads: 'All leads',
    unexpectedError: 'Unexpected error.',
  },
  apiInbox: {
    httpStreamStatus: 'HTTP stream {{status}}',
  },
  apiInsights: {
    theAiServiceOrTheSource: 'The AI service or the source data is temporarily unavailable.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance.',
  },
  apiInsightsReports: {
    keyIndicatorsKpis: 'Key indicators (KPIs)',
    engagementCurve: 'Engagement curve',
    followerGrowth: 'Follower growth',
    bestSlots: 'Best slots',
    topPosts: 'Top posts',
    competitors: 'Competitors',
    followers: 'Followers',
    scope: 'Scope',
    totalEngagement: 'Total engagement',
    averageEngagementRate: 'Average engagement rate',
    noTrackedCompetitorAddSomeTo: 'No tracked competitor — add some to compare.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsReadOnly: 'Access denied — insufficient rights (read-only role).',
  },
  apiIntegrationsHub: {
    other: 'Other',
  },
  apiIntelligence: {
    thisFeatureIsNotYetAvailable: 'This feature is not yet available on this backend.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsReadOnly: 'Access denied — insufficient rights (read-only role).',
  },
  apiLearning: {
    confirmed: 'Confirmed',
    pinned: 'Pinned',
    emerging: 'Emerging',
    declining: 'Declining',
    hidden: 'Hidden',
  },
  apiListening: {
    errorStatus: 'Error {{status}}',
    theActionFailed: 'The action failed.',
  },
  apiLongform: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to generate with the model.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again in a moment — nothing was made up.',
    thisFeatureIsNotYetAvailable: 'This feature is not yet available / configured on this backend.',
  },
  apiMarketingStrategy: {
    positioningFor: 'For {{for}}',
    positioningWho: 'who {{who}}',
    unlikeUnlike: 'unlike {{unlike}}',
    uniqueValuePropositionUsp: 'Unique value proposition: {{usp}}',
    rolesV: 'Roles: {{v}}',
    keywordsV: 'Keywords: {{v}}',
    content: 'Content',
    ads: 'Ads',
    community: 'Community',
    nurturing: 'Nurturing',
    prospecting: 'Prospecting',
    contentCalendar: 'Content calendar',
    advertisingPlan: 'Advertising plan',
    communityEngagement: 'Community engagement',
    audienceTargeting: 'Audience targeting',
    nurturingSequence: 'Nurturing sequence',
    funnelLandingPage: 'Funnel / landing page',
    b2bProspecting: 'B2B prospecting',
    actionV: 'Action {{v}}',
    plannerUnavailableEmptyDraft: 'Planner unavailable — empty draft.',
    onTrack: 'On track',
    risky: 'Risky',
    behind: 'Behind',
    disabled: 'Disabled',
    withApproval: 'With approval',
    automatic: 'Automatic',
    totalObjectiveSMeasured: '{{total}} objective(s) measured',
    behindBehind: '{{behind}} behind',
    atRiskAtRisk: '{{at_risk}} at risk',
    onTrackOnTrack: '{{on_track}} on track',
    recommendation: 'Recommendation',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to generate the strategy.',
    theAiServiceIsTemporarilyUnavailable: 'The AI service is temporarily unavailable. Try again — nothing was made up.',
    thisCapabilityIsNotYetAvailable: 'This capability is not yet available on this backend.',
    unavailable: 'unavailable',
  },
  apiMarketplace: {
    quality: 'Quality',
    communication: 'Communication',
    timeliness: 'Timeliness',
    expertise: 'Expertise',
    valueForMoney: 'Value for money',
    requested: 'Requested',
    accepted: 'Accepted',
    inProgress: 'In progress',
    done: 'Done',
    cancelled: 'Cancelled',
    declined: 'Declined',
  },
  apiMas: {
    l0Manual: 'L0 · Manual',
    manual: 'Manual',
    manualProposesOnlyNoAutomaticExecution: 'Manual — proposes only, no automatic execution. Even the task waits for an explicit human action; if a tool is invoked anyway, the call is refused. “Stop everything” switch for this persona.',
    l1Assisted: 'L1 · Assisted',
    assisted: 'Assisted',
    theAiOnlySuggestsAHuman: 'The AI only suggests — a human decides and executes. Recommended for strategic analyses and recommendations.',
    l2Supervised: 'L2 · Supervised',
    supervised: 'Supervised',
    theAiExecutesButWaitsFor: 'The AI executes but waits for human approval before any external send or publication. The safest default.',
    l3Delegated: 'L3 · Delegated',
    delegated: 'Delegated',
    theAiExecutesDirectlyThenNotifies: 'The AI executes directly, then notifies a human afterwards (reports, alerts, internal planning).',
    l4Autonomous: 'L4 · Autonomous',
    autonomous: 'Autonomous',
    theAiExecutesRoutineTasksFully: 'The AI executes routine tasks fully autonomously, without notification — only traced in the audit.',
    l5FullyAutonomous: 'L5 · Fully autonomous',
    fullyAutonomous: 'Fully autonomous',
    fullyAutonomousExecutesWithoutApprovalIn: 'Fully autonomous — executes without approval, including external sends/publications (the CASL/compliance guard still applies to sends).',
  },
  apiMedia: {
    imageGenerationViaAiAiOrchestrator: 'Image generation via AI (ai-orchestrator).',
    aiVideoGenerationRequiresAProvider: 'AI video generation (requires a provider key on the server).',
    aiAnimationGenerationGifLottieIs: 'AI animation generation (GIF/Lottie) is coming soon. Upload a GIF or import it from a URL in the meantime.',
    image: 'Image',
    video: 'Video',
    event: 'Event',
    file: 'File',
    audio: 'Audio',
    invalidMediaResponse: 'Invalid media response.',
    uploadFailedStatus: 'Upload failed ({{status}}).',
    networkErrorDuringUpload: 'Network error during upload.',
    videoGenerationNotConfigured: 'Video generation not configured.',
  },
  apiMediaGen: {
    video: 'Video',
    imageVideo: 'Image → video',
    noProviderConfigured: 'no provider configured',
    noModelAvailable: 'no model available',
    noImageModelAvailable: 'no image model available',
    noVideoModelAvailable: 'no video model available',
    noAvatarModelAvailable: 'no avatar model available',
    providerKeyMissingOnTheServer: 'provider key missing on the server',
    notConfiguredOnTheServer: 'not configured on the server',
    thePersonSConsentIsRequired: 'the person\'s consent is required',
    notCurrentlyAvailable: 'not currently available',
  },
  apiMonitoring: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    actionReservedForAnEditorAdministrator: 'Action reserved for an editor/administrator role — your role is read-only.',
    notFoundMaybeAlreadyDeleted: 'Not found (maybe already deleted).',
  },
  apiNotifications: {
    aiAgentAction: 'AI agent action',
    decisionRequired: 'Decision required',
    slaEscalation: 'SLA escalation',
    deliverableReady: 'Deliverable ready',
    agentQuestion: 'Agent question',
    prospectAssigned: 'Prospect assigned',
    shareReceived: 'Share received',
    shareRemoved: 'Share removed',
    invitationAccepted: 'Invitation accepted',
    roleChanged: 'Role changed',
    memberRemoved: 'Member removed',
    mentionInANote: 'Mention in a note',
    approvalRequested: 'Approval requested',
    postApproved: 'Post approved',
    postRejected: 'Post rejected',
    reminderBeforePublishing: 'Reminder before publishing',
    postPublished: 'Post published',
    publishingFailed: 'Publishing failed',
    notification: 'Notification',
    aiAgent: 'AI agent',
    publishing: 'Publishing',
  },
  apiOfferings: {
    kingdomConnection: 'Kingdom Connection',
    christianConnectionAndCommunityAppKconne: 'Christian connection and community app (kconnection.org, Google Play).',
    connectFrenchSpeakingBelieversChurchesAn: 'Connect French-speaking believers, churches and ministries around their vision and mission.',
    pastor: 'Pastor',
    communicationsManager: 'Communications manager',
    ministryDirector: 'Ministry director',
    church: 'church',
    ministry: 'ministry',
    christianCommunity: 'Christian community',
    kimmobiliaRealEstatePlatformAndServices: 'Kimmobilia real-estate platform and services.',
    helpBrokersAgenciesAndInvestorsFind: 'Help brokers, agencies and investors find, manage and sell faster with a modern real-estate platform.',
    realEstateBroker: 'Real estate broker',
    agencyDirector: 'Agency director',
    investor: 'Investor',
    realEstateAgency: 'real-estate agency',
  },
  apiOrg: {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    guest: 'Guest',
    active: 'Active',
    suspended: 'Suspended',
  },
  apiPlanGate: {
    nameIsNotIncludedInYour: '“{{name}}” is not included in your plan — upgrade to a higher plan to access it.',
  },
  apiPlanning: {
    quickPostV: 'Quick post — {{v}}',
    createdFromTheCalendar: 'Created from the calendar',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRights: 'Access denied — insufficient rights.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance.',
    thisCapabilityIsNotYetDeployed: 'This capability is not yet deployed on your environment.',
    serviceTemporarilyUnavailableTryAgainIn: 'Service temporarily unavailable. Try again in a moment.',
  },
  apiPortal: {
    errorStatus: 'Error {{status}}',
    couldNotReachTheServerCheck: 'Could not reach the server. Check your connection.',
    thisLinkIsInvalidOrHas: 'This link is invalid or has expired. Ask the sender for a new link.',
    thisItemHasAlreadyBeenHandled: 'This item has already been handled.',
  },
  apiPostReview: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRights: 'Access denied — insufficient rights.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to start the review.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again in a moment.',
  },
  apiProspecting: {
    defineAtLeastOneDiscoveryCriterion: 'Define at least one discovery criterion (industry, job title, social platform…) before confirming.',
    partialResultsDegradedSource: 'partial results (degraded source)',
    describedObjective: 'Described objective',
    describeYourObjectiveMorePrecisely10: 'Describe your objective more precisely (10 characters min.).',
    jobTitle: 'Job title',
    seniority: 'Seniority',
    companyHeadcount: 'Company headcount',
    theMinimumExceedsTheMaximum: 'The minimum exceeds the maximum.',
    revenue: 'Revenue',
    chooseASocialPlatform: 'Choose a social platform.',
    instagramFacebookEnterTheAccountHandles: 'Instagram / Facebook: enter the account handles (never “all followers”).',
    addATopicOrAFollower: 'Add a topic or a follower threshold for this platform.',
    minFollowersFollowers: '≥ {{min_followers}} followers',
    socialDiscovery: 'Social discovery',
    discovery: 'Discovery',
    scoring: 'Scoring',
    enrichment: 'Enrichment',
    grouping: 'Grouping',
    tracking: 'Tracking',
    enrollment: 'Enrollment',
    pending: 'Pending',
    inProgress: 'In progress',
    done: 'Done',
    skipped: 'Skipped',
    error: 'Error',
    industry: 'Industry',
    localization: 'Localization',
  },
  apiProspection: {
    everyHour: 'Every hour',
    daily: 'Daily',
    weekly: 'Weekly',
  },
  apiProspectionCalendar: {
    linkedinManual: 'LinkedIn (manual)',
    defaultD0D5D10D15D20: 'Default (D0 · D5 · D10 · D15 · D20)',
    tightEvery3Days: 'Tight (~ every 3 days)',
    spacedEveryWeek: 'Spaced (~ every week)',
    custom: 'Custom',
    scheduled: 'Scheduled',
    reminderCreated: 'Reminder created',
    blockedCompliance: 'Blocked (compliance)',
    reminderFailed: 'Reminder failed',
  },
  apiProspectionStrategy: {
    linkedinManual: 'LinkedIn (manual)',
    recommendation: 'Recommendation',
    default: 'Default',
    tight: 'Tight',
    spaced: 'Spaced',
    custom: 'Custom',
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
    phone: 'Phone',
    event: 'Event',
  },
  apiPublicApprovals: {
    errorStatus: 'Error {{status}}',
    couldNotReachTheServerCheck: 'Could not reach the server. Check your connection.',
    thisReviewHasAlreadyBeenHandled: 'This review has already been handled.',
    thisLinkIsInvalidOrHas: 'This link is invalid or has expired. Ask the sender for a new link.',
  },
  apiPublishing: {
    likes: 'Likes',
    celebrate: 'Celebrate',
    support: 'Support',
    love: 'Love',
    insightful: 'Insightful',
    funny: 'Funny',
    errorStatus: 'Error {{status}}',
    unknownError: 'Unknown error',
  },
  apiPublishingReminders: {
    n7DaysBefore: '7 days before',
    n24HBefore: '24 h before',
    n3HBefore: '3 h before',
    n1HBefore: '1 h before',
    n15MinBefore: '15 min before',
  },
  apiQuiz: {
    singleChoice: 'Single choice',
    multipleChoice: 'Multiple choice',
    freeAnswer: 'Free answer',
    step1: 'Step 1',
    result: 'Result',
    addAtLeastOneStep: 'Add at least one step.',
    addAtLeastOneResultPage: 'Add at least one result page.',
    whereUnknownResultV: '{{where}}: unknown result “{{v}}”.',
    whereUnknownStepV: '{{where}}: unknown step “{{v}}”.',
    stepVAddAtLeastOne: 'Step {{v}}: add at least one question.',
    stepVNext: 'Step {{v}} (next)',
    stepVQuestionV2: 'Step {{v}}, question {{v2}}',
    whereTheTextIsRequired: '{{where}}: the text is required.',
    whereAtLeast2Options: '{{where}}: at least 2 options.',
    whereOptionVLabelRequired: '{{where}}, option {{v}}: label required.',
    resultVTheTitleIsRequired: 'Result {{v}}: the title is required.',
    resultVMinMax: 'Result {{v}}: min > max.',
    atLeastOneResultMustHave: 'At least one result must have a score threshold or be “default”.',
    theBranchingDoesNotTerminate: 'The branching does not terminate.',
    theQuestionVIsRequired: 'The question “{{v}}” is required.',
    noResultPageMatchesThisScore: 'No result page matches this score.',
    serviceTemporarilyUnavailable: 'Service temporarily unavailable.',
  },
  apiRepurpose: {
    errorStatus: 'Error {{status}}',
  },
  apiReviews: {
    errorStatus: 'Error {{status}}',
    theActionFailed: 'The action failed.',
  },
  apiSchedules: {
    weeks: 'weeks',
    months: 'months',
    years: 'years',
    everyDay: 'Every day',
    everyWeek: 'Every week',
    everyMonth: 'Every month',
    weekdaysMonFri: 'Weekdays (Mon–Fri)',
    everyHour: 'Every hour',
    everyYear: 'Every year',
    everyIntervalPlural: 'Every {{interval}} {{plural}}',
    everyWeekDays: 'Every week · {{days}}',
    wholeTeam: 'Whole team',
    hours: 'hours',
    days: 'days',
  },
  apiSending: {
    errorStatus: 'Error {{status}}',
    pending: 'Pending',
    active: 'Active',
    paused: 'Paused',
    suspended: 'Suspended',
    notMeasured: 'Not measured',
    good: 'Good',
    fair: 'Fair',
    low: 'Low',
  },
  apiSocialDiscovery: {
    accountNotConnected: 'Account not connected.',
  },
  apiSocialListening: {
    brand: 'Brand',
    keyword: 'Keyword',
    competitor: 'Competitor',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsReadOnly: 'Access denied — insufficient rights (read-only role).',
  },
  apiSourceStudio: {
    briefing: 'Briefing',
    studyGuide: 'Study guide',
    faq: 'FAQ',
    timeline: 'Timeline',
    mindMap: 'Mind map',
    podcastScript: 'Podcast script',
    document: 'Document',
    text: 'Text',
    video: 'Video',
    pending: 'Pending',
    processing: 'Processing…',
    ready: 'Ready',
    error: 'Error',
    link: 'Link',
  },
  apiStaff: {
    lead: 'Lead',
    junior: 'Junior',
    confirmed: 'Confirmed',
    senior: 'Senior',
    direction: 'Direction',
    active: 'Active',
    paused: 'Paused',
    disabled: 'Disabled',
    available: 'Available',
    atWork: 'At work',
    draft: 'Draft',
    publishing: 'Publishing',
    report: 'Report',
    analysis: 'Analysis',
    deliverable: 'Deliverable',
    visual: 'Visual',
  },
  apiStaffQuestions: {
    serviceTemporarilyUnavailableCheckYourCo: 'Service temporarily unavailable. Check your connection.',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    answeringOnTheOwnerSBehalf: 'Answering on the owner\'s behalf is reserved for the owner or an administrator.',
    questionNotFoundTaskDeletedOr: 'Question not found (task deleted or outside your organization).',
    thisQuestionIsNoLongerPending: 'This question is no longer pending — the task has already resumed (answered elsewhere).',
    aNonEmptyAnswerIsRequired: 'A non-empty answer is required.',
    tooManyRequestsLimitTemporarilyReached: 'Too many requests — limit temporarily reached. Wait a moment, then try again.',
  },
  apiSuperadmin: {
    unnamedOrganization: 'Unnamed organization',
  },
  apiTeams: {
    yourPlanDoesNotAllowThis: 'Your plan does not allow this action — upgrade to a higher plan.',
    unnamedTeam: 'Unnamed team',
    automatic: 'Automatic',
    manualSelection: 'Manual selection',
    sized: 'Sized',
    unnamedTemplate: 'Unnamed template',
    untitledTask: 'Untitled task',
    pendingPool: 'Pending (pool)',
    assignedAwaitingDispatch: 'Assigned — awaiting dispatch',
    scheduled: 'Scheduled',
    ready: 'Ready',
    inProgress: 'In progress',
    blocked: 'Blocked',
    awaitingApproval: 'Awaiting approval',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
  },
  apiTelephony: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance in Billing.',
  },
  apiTranslate: {
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRightsForThis: 'Access denied — insufficient rights for this action.',
    insufficientAiCreditsTopUpYour: 'Insufficient AI credits — top up your balance to translate.',
    translationRequiresAnAiModelKey: 'Translation requires an AI model key, and none is configured.',
    theAiModelIsTemporarilyUnavailable: 'The AI model is temporarily unavailable. Try again — nothing was made up.',
  },
  apiVideoStudio: {
    thisFeatureRequiresAProviderKey: 'This feature requires a provider key or ffmpeg on the service (not configured).',
    sessionExpiredSignInAgain: 'Session expired — sign in again.',
    accessDeniedInsufficientRights: 'Access denied — insufficient rights.',
  },
  apiVirality: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    exceptional: 'Exceptional',
  },
  apiVisitors: {
    unexpectedError: 'Unexpected error.',
  },
  apiVoice: {
    speechRecognitionTemporarilyUnavailable: 'Speech recognition temporarily unavailable.',
    audioTooLongShortenYourRecording: 'Audio too long — shorten your recording.',
  },
  apiWebListening: {
    news: 'News',
    blogs: 'Blogs',
    forums: 'Forums',
    reviewSites: 'Review sites',
    web: 'Web',
  },
  diagramMedia: {
    invalidDiagramImage: 'Invalid diagram image.',
    diagram: 'Diagram',
    diagramUrlMissingFromTheResponse: 'Diagram URL missing from the response.',
  },
  ep6Ui: {
    beingDeployed: 'Being deployed —',
    isNotYetAvailableOnThis: 'is not yet available on this backend. The service is being delivered; this page will show automatically as soon as it is online. No data is simulated.',
    loading: 'Loading…',
    notAvailable: 'not available',
    officialData: 'Official data',
    partialCoverage: 'Partial coverage',
    noOfficialData: 'No official data',
  },
  money: {
    cad: 'CAD',
    cadCanadianDollarCa: 'CAD — Canadian dollar (CA$)',
    usd: 'USD',
    usdUsDollarUs: 'USD — US dollar (US$)',
    eurEuro: 'EUR — Euro (€)',
    gbpPoundSterling: 'GBP — Pound sterling (£)',
    audAustralianDollarA: 'AUD — Australian dollar (A$)',
    chfSwissFranc: 'CHF — Swiss franc',
  },
  apiApprovals: {
    approvedTheAgentTaskResumes: 'Approved — the agent task resumes execution.',
    rejectedTheAgentTaskIsCancelled: 'Rejected — the agent task is cancelled.',
    decisionSavedLinkedTaskNotFound: 'Decision saved — the linked task could not be found.',
    decisionSavedTaskNoLongerAwaitingApproval: 'Decision saved — the task was no longer awaiting approval (status: {{status}}).',
    unknownStatus: 'unknown',
    decisionSavedButTaskResumeFailed: 'Decision saved, but resuming the task failed.',
  },
  apiWorkflowsExtras: {
    wouldCallTool: 'Tool “{{tool}}” {{args}}',
    wouldCallLlmWithModel: 'AI call ({{model}}): {{prompt}}',
    wouldCallLlm: 'AI call: {{prompt}}',
    wouldCallNotify: 'Notification: {{message}}',
    wouldCallSequenceEnroll: 'Enrolment in sequence {{sequence}}',
    wouldCallApprovalRequest: 'Approval request (not created)',
    wouldCallGrowthAction: 'Growth action “{{action}}”',
  },
  apiPortalLinks: {
    n24Hours: '24 hours',
    n3Days: '3 days',
    n7Days: '7 days',
    n30Days: '30 days',
  },
};

export default en;
